//Tamil-Katral-Maiyam\src\pages\admin\TeachersList.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/Textarea';
import { Input } from '../../components/ui/Input';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { formatDate } from '../../utils/format';
import { validateEmail, validatePersonName, stripDigitsFromName } from '../../utils/validation';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSchoolProfile,
  inviteTeacher,
  listCreatedInvites,
  CreatedInvite,
  InvitePayload,
} from '../../modules/admin/api/school.client';
import {
  listTeachers,
  getTeacher,
  createTeacher,
  CreateTeacherPayload,
  RosterTeacher,
} from '../../modules/roster/api/roster.client';
import { BulkImportModal } from '../../components/admin/BulkImportModal';
import { ScrollableSelect } from '../../components/ui/ScrollableSelect';
import { Eye, Mail } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

type AddTeacherFormState = Omit<CreateTeacherPayload, 'display_name'>;

const EMAIL_MAX_LENGTH = 50;
const NOTES_MAX_LENGTH = 100;
const INVITE_NOTES_MAX_LENGTH = 150;

export const TeachersList: React.FC = () => {
  const [teachers, setTeachers] = useState<RosterTeacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
 
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [addTeacherErrors, setAddTeacherErrors] = useState<Record<string, string>>({});
  const [teacherForm, setTeacherForm] = useState<AddTeacherFormState>({
    email: '',
    phone: '',
    first_name: '',
    last_name: '',
    employee_code: '',
    joining_date: '',
    notes: '',
  });
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const schoolId = user?.memberships?.[0]?.school_id;
  const [schoolName, setSchoolName] = useState('');
  const [sendInviteNow, setSendInviteNow] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<RosterTeacher | null>(null);
  const [inviteForm, setInviteForm] = useState({ email: '', invitee_name: '', notes: '' });
  const [isInviting, setIsInviting] = useState(false);
  const [teacherInvites, setTeacherInvites] = useState<CreatedInvite[]>([]);
  const inviteByEmail = useMemo(() => {
    const map: Record<string, CreatedInvite> = {};
    teacherInvites.forEach((invite) => {
      if (invite.email) {
        map[invite.email.toLowerCase()] = invite;
      }
    });
    return map;
  }, [teacherInvites]);

  const filteredTeachers = useMemo(() => {
    return teachers.filter((t) => {
      const displayName = (t.display_name || '').toLowerCase();
      const email = (t.email || '').toLowerCase();
      const phone = (t.phone || '').replace(/\s/g, '');
      const search = searchText.trim().toLowerCase().replace(/\s/g, '');
      if (search) {
        const matchSearch =
          displayName.includes(search) || email.includes(search) || phone.includes(search);
        if (!matchSearch) return false;
      }
      if (statusFilter !== 'all' && (t.status || '') !== statusFilter) return false;
      // grade filter: match any class.grade equals selected grade
      if (gradeFilter !== 'all') {
        const classes = (t as any).classes ?? [];
        const hasGrade = classes.some((c: any) => String(c?.grade) === String(gradeFilter));
        if (!hasGrade) return false;
      }
      return true;
    });
  }, [teachers, searchText, statusFilter, gradeFilter]);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    teachers.forEach((t) => {
      if (t.status) set.add(t.status);
    });
    return [
      { value: 'all', label: 'All statuses' },
      ...Array.from(set).map((v) => ({ value: v, label: v })),
    ];    
  }, [teachers]);

  const hasActiveFilters = Boolean(searchText.trim() || statusFilter !== 'all' || gradeFilter !== 'all');
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setGradeFilter('all');
    // no need to re-fetch; filteredTeachers will show full list
  };

  const gradeOptions = useMemo(() => {
    const set = new Set<string>();
    (teachers || []).forEach((t: any) => {
      (t.classes || []).forEach((c: any) => {
        if (c?.grade) set.add(String(c.grade));
      });
    });
    return Array.from(set)
      .map((g) => String(g))
      .sort((a, b) => {
        const na = Number(a);
        const nb = Number(b);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
        return a.localeCompare(b);
      });
  }, [teachers]);

  const placeholders = useMemo(
    () => ({
      first_name: 'Enter first name',
      last_name: 'Enter last name',
      email: 'name@gmail.com',
      phone: 'Enter phone number',
      employee_code: 'Enter employee code (e.g., EMP-001)',
      joining_date: 'Select joining date',
      notes: 'Enter notes (optional)',
      invite_email: 'name@gmail.com',
      invitee_name: 'Enter invitee name',
      invite_notes: 'Enter notes (optional)',
    }),
    []
  );

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const items = await listTeachers({ limit: 50, skip: 0, include: 'classes,students,sections' });
      setTeachers(items);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeachers();
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    const fetchSchool = async () => {
      try {
        const profile = await getSchoolProfile(schoolId);
        setSchoolName(profile.name || '');
      } catch {
        // ignore
      }
    };
    fetchSchool();
  }, [schoolId]);

  const loadTeacherInvites = async () => {
    if (!schoolId) return;
    try {
      const invites = await listCreatedInvites(schoolId, 'teacher');
      setTeacherInvites(invites);
    } catch (error) {
      console.error('Failed to load teacher invites', error);
    }
  };

  useEffect(() => {
    loadTeacherInvites();
  }, [schoolId]);

  const openDetail = (teacherId: string) => {
    navigate(`/admin/teachers/${teacherId}`);
  };

  const field = (v: string | null | undefined): string =>
    v != null && String(v).trim() !== '' ? String(v).trim() : '-';

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAddTeacherErrors({});
    const display_name = [teacherForm.first_name, teacherForm.last_name].filter(Boolean).join(' ').trim();
    const errors: Record<string, string> = {};
    if (!display_name) {
      errors.first_name = 'At least first name or last name is required';
    }
    const firstName = teacherForm.first_name ?? '';
    const lastName = teacherForm.last_name ?? '';
    if (firstName.trim()) {
      const nameResult = validatePersonName(firstName);
      if (!nameResult.valid) {
        errors.first_name = nameResult.message ?? 'First name and last name should not contain numbers';
      }
    }
    if (lastName.trim()) {
      const nameResult = validatePersonName(lastName);
      if (!nameResult.valid) {
        errors.last_name = nameResult.message ?? 'First name and last name should not contain numbers';
      }
    }
    const emailTrim = teacherForm.email.trim();
    if (!emailTrim) {
      errors.email = 'Email is required';
    } else if (!validateEmail(emailTrim)) {
      errors.email = 'Please enter a valid email address';
    }
    if (teacherForm.phone && /[a-zA-Z]/.test(teacherForm.phone)) {
      errors.phone = 'Phone should contain only numbers and country code';
    }
    if (teacherForm.joining_date && teacherForm.joining_date.trim() !== '') {
      const d = new Date(teacherForm.joining_date);
      if (Number.isNaN(d.getTime())) {
        errors.joining_date = 'Please enter a valid date';
      }
    }
    if (Object.keys(errors).length > 0) {
      setAddTeacherErrors(errors);
      const firstMessage = Object.values(errors)[0];
      if (firstMessage) showErrorToast(firstMessage);
      return;
    }
    setIsCreating(true);
    try {
      const payload: CreateTeacherPayload = { ...teacherForm, display_name };
      const createdTeacher = await createTeacher(payload);
      let inviteError = false;
      if (sendInviteNow && schoolId) {
        try {
          const newTeacherId = resolveTeacherProfileId(createdTeacher);
          if (!newTeacherId) {
            throw new Error('Unable to resolve teacher profile id for invite');
          }
          const inviteNotes = `Invite teacher to ${schoolName || 'your school'}`;
          const invitePayload = {
            email: teacherForm.email,
            invitee_name: display_name,
            notes: inviteNotes,
            teacher_profile_id: newTeacherId,
            metadata: { teacher_id: newTeacherId, notes: inviteNotes },
          } as InvitePayload & { teacher_profile_id: string };
          await inviteTeacher(schoolId, invitePayload);
        } catch (error) {
          inviteError = true;
          showErrorToast('Teacher created but invite failed');
        }
      }
      if (!inviteError) {
        showSuccessToast(sendInviteNow ? 'Teacher created and invite sent' : 'Teacher created');
      }
      setAddModalOpen(false);
      setAddTeacherErrors({});
      setTeacherForm({
        email: '',
        phone: '',
        first_name: '',
        last_name: '',
        employee_code: '',
        joining_date: '',
        notes: '',
      });
      setSendInviteNow(true);
      await loadTeachers();
      await loadTeacherInvites();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsCreating(false);
    }
  };

  const getInviteStatus = (teacher: RosterTeacher) => {
    if (teacher.platform_user_id) return 'Joined';
    const emailKey = (teacher.email || '').toLowerCase();
    const invite = inviteByEmail[emailKey];
    if (invite?.status === 'accepted') return 'Joined';
    if (invite) return `Invited (${invite.status})`;
    return 'Not invited';
  };

  const getTeacherDisplayStatus = (teacher: RosterTeacher): 'Deleted' | 'Active' | 'Invited' | 'Draft' => {
    if (teacher.deleted_at != null && String(teacher.deleted_at).trim() !== '') return 'Deleted';
    if (teacher.platform_user_id != null && String(teacher.platform_user_id).trim() !== '') return 'Active';
    const inv = getInviteStatus(teacher);
    if (inv !== 'Joined' && inv !== 'Not invited') return 'Invited';
    return 'Draft';
  };

  const isTeacherLinked = (teacher: RosterTeacher) =>
    teacher.platform_user_id != null && String(teacher.platform_user_id).trim() !== '';

  const resolveTeacherProfileId = (teacher?: RosterTeacher): string => teacher?._id ?? teacher?.id ?? '';

  const handleInviteSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!schoolId) {
      showErrorToast('School context missing');
      return;
    }
    setIsInviting(true);
    try {
      const targetId = resolveTeacherProfileId(inviteTarget ?? undefined);
      if (!targetId) {
        showErrorToast('Teacher identifier missing for invite');
        return;
      }
      const inviteNotes = inviteForm.notes || `Invite teacher to ${schoolName || 'your school'}`;
      const invitePayload = {
        email: inviteForm.email,
        invitee_name: inviteForm.invitee_name,
        notes: inviteNotes,
        teacher_profile_id: targetId,
        metadata: { teacher_id: targetId, notes: inviteNotes },
      } as InvitePayload & { teacher_profile_id: string };
      await inviteTeacher(schoolId, invitePayload);
      showSuccessToast('Invite sent');
      setInviteModalOpen(false);
      setInviteTarget(null);
      setInviteForm({ email: '', invitee_name: '', notes: '' });
      await loadTeachers();
      await loadTeacherInvites();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsInviting(false);
    }
  };

  const openInviteModal = (teacher: RosterTeacher) => {
    setInviteTarget(teacher);
    setInviteForm({
      email: teacher.email || '',
      invitee_name: teacher.display_name || `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim(),
      notes: '',
    });
    setInviteModalOpen(true);
  };

  return (
    <Layout>
      <PageHeader
        title="Teachers"
        subtitle="Manage your teaching staff"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setImportModalOpen(true)}>
              Import
            </Button>
            <Button onClick={() => setAddModalOpen(true)}>Add Teacher</Button>
          </div>
        }
      />

      <Card>
        <CardContent padding="md">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center mb-4">
            <div className="md:col-span-6">
              <Input
                placeholder="Search by name, email, phone..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-11 w-full"
              />
            </div>
            <div className="md:col-span-2 w-full [&_button]:h-11">
              <ScrollableSelect
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="Status"
                maxListHeight="10rem"
                className="w-full"
              />
            </div>

            <div className="md:col-span-2 w-full [&_button]:h-11">
              <ScrollableSelect
                options={[
                  { value: 'all', label: 'All grades' },
                  ...gradeOptions.map((g) => ({ value: g, label: `Grade ${g}` })),
                ]}
                value={gradeFilter}
                onChange={setGradeFilter}
                placeholder="All grades"
                maxListHeight="10rem"
                className="w-full"
              />
            </div>

            <div className="col-span-1 md:col-span-2 flex md:justify-end items-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                Clear
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner size="lg" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTeachers.map((teacher) => {
                  const inviteStatus = getInviteStatus(teacher);
                  const displayStatus = getTeacherDisplayStatus(teacher);
                  const linked = isTeacherLinked(teacher);
                  const canInvite = Boolean(teacher.email?.trim()) && !linked;
                  return (
                    <TableRow key={teacher.id}>
                      <TableCell>{teacher.first_name || `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim() || '-'}</TableCell>
                      <TableCell>{teacher.email || '-'}</TableCell>
                      <TableCell>{teacher.phone || '-'}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            displayStatus === 'Active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : displayStatus === 'Invited'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                : displayStatus === 'Deleted'
                                  ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                  : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}
                        >
                          {displayStatus}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            onClick={() => openDetail(teacher.id)}
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canInvite && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5"
                              onClick={() => openInviteModal(teacher)}
                              title={inviteStatus !== 'Not invited' ? 'Resend invite' : 'Invite'}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={isAddModalOpen} onClose={() => { setAddModalOpen(false); setAddTeacherErrors({}); }} title="Add Teacher">
        <form className="flex flex-col max-h-[90vh] overflow-hidden" onSubmit={handleCreateSubmit}>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
            <Input
              label="First Name"
              placeholder={placeholders.first_name}
              value={teacherForm.first_name}
              error={addTeacherErrors.first_name}
              onChange={(event) => {
                const sanitized = stripDigitsFromName(event.target.value);
                setTeacherForm((prev) => ({ ...prev, first_name: sanitized }));
                if (addTeacherErrors.first_name) setAddTeacherErrors((e) => ({ ...e, first_name: '' }));
              }}
            />
            <Input
              label="Last Name"
              placeholder={placeholders.last_name}
              value={teacherForm.last_name}
              error={addTeacherErrors.last_name}
              onChange={(event) => {
                const sanitized = stripDigitsFromName(event.target.value);
                setTeacherForm((prev) => ({ ...prev, last_name: sanitized }));
                if (addTeacherErrors.last_name) setAddTeacherErrors((e) => ({ ...e, last_name: '' }));
              }}
            />
            <Input
              label="Email"
              required
              type="email"
              maxLength={EMAIL_MAX_LENGTH}
              placeholder={placeholders.email}
              value={teacherForm.email}
              error={addTeacherErrors.email}
              onChange={(event) => {
                const value = event.target.value.slice(0, EMAIL_MAX_LENGTH);
                setTeacherForm((prev) => ({ ...prev, email: value }));
                if (addTeacherErrors.email) setAddTeacherErrors((e) => ({ ...e, email: '' }));
              }}
            />
            <div className="w-full">
              <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1" htmlFor="add-teacher-phone">
                Phone
              </label>
              <PhoneInput
                country="us"
                value={teacherForm.phone?.replace(/^\+/, '') ?? ''}
                onChange={(value) => {
                  setTeacherForm((prev) => ({
                    ...prev,
                    phone: value ? `+${value}` : '',
                  }));
                  if (addTeacherErrors.phone) setAddTeacherErrors((e) => ({ ...e, phone: '' }));
                }}
                inputStyle={{
                  width: '100%',
                  height: '42px',
                  borderRadius: '0.5rem',
                  borderColor: addTeacherErrors.phone ? 'var(--error-500, #ef4444)' : undefined,
                }}
                containerStyle={{ width: '100%' }}
                dropdownStyle={{ zIndex: 9999 }}
                buttonStyle={{
                  borderRadius: '0.5rem 0 0 0.5rem',
                }}
                inputProps={{
                  name: 'phone',
                  id: 'add-teacher-phone',
                }}
                containerClass="add-teacher-phone-input"
              />
              {addTeacherErrors.phone && (
                <p className="mt-1 text-sm text-error-600 dark:text-error-400">{addTeacherErrors.phone}</p>
              )}
            </div>
            <Input
              label="Employee Code"
              placeholder={placeholders.employee_code}
              value={teacherForm.employee_code}
              onChange={(event) => setTeacherForm((prev) => ({ ...prev, employee_code: event.target.value }))}
            />
            <Input
              label="Joining Date"
              type="date"
              placeholder={placeholders.joining_date}
              value={teacherForm.joining_date || ''}
              error={addTeacherErrors.joining_date}
              onChange={(event) => {
                setTeacherForm((prev) => ({ ...prev, joining_date: event.target.value }));
                if (addTeacherErrors.joining_date) setAddTeacherErrors((e) => ({ ...e, joining_date: '' }));
              }}
            />
            <Textarea
              label="Notes"
              placeholder={placeholders.notes}
              maxLength={NOTES_MAX_LENGTH}
              value={teacherForm.notes}
              helperText={`${(teacherForm.notes || '').length} / ${NOTES_MAX_LENGTH}`}
              onChange={(event) => {
                const value = event.target.value.slice(0, NOTES_MAX_LENGTH);
                setTeacherForm((prev) => ({ ...prev, notes: value }));
              }}
            />
          </div>
          <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 px-0 py-4 flex items-start justify-between gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 rounded"
                checked={sendInviteNow}
                onChange={(event) => setSendInviteNow(event.target.checked)}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Send invite now</span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAddModalOpen(false)} type="button">
                Cancel
              </Button>
              <Button type="submit" isLoading={isCreating}>
                Create
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal isOpen={inviteModalOpen} onClose={() => setInviteModalOpen(false)} title="Invite Teacher">
        <form className="space-y-4" onSubmit={handleInviteSubmit}>
          <Input
            label="Email"
            required
            type="email"
            placeholder={placeholders.invite_email}
            value={inviteForm.email}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
            readOnly
          />
          <Input
            label="Invitee Name"
            required
            placeholder={placeholders.invitee_name}
            value={inviteForm.invitee_name}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, invitee_name: event.target.value }))}
            readOnly
          />
          <Input
            label="Notes"
            placeholder={placeholders.invite_notes}
            value={inviteForm.notes}
            maxLength={INVITE_NOTES_MAX_LENGTH}
            helperText={`${(inviteForm.notes || '').length} / ${INVITE_NOTES_MAX_LENGTH}`}
            onChange={(event) => {
              const value = event.target.value.slice(0, INVITE_NOTES_MAX_LENGTH);
              setInviteForm((prev) => ({ ...prev, notes: value }));
            }}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setInviteModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isInviting}>
              Send Invite
            </Button>
          </div>
        </form>
      </Modal>

      {/* Detail modal removed — navigation to /admin/teachers/:teacherId now used */}

      <BulkImportModal isOpen={isImportModalOpen} onClose={() => setImportModalOpen(false)} />
    </Layout>
  );
};
